/**
 * Calculs monétaires d'une facture.
 *
 * Tout est calculé en CENTIMES ENTIERS puis reconverti en euros à la sortie :
 * en flottant, 3 * 0.1 vaut 0.30000000000000004 et une facture finit par
 * afficher un total faux d'un centime.
 *
 * Module pur : aucune dépendance à Vue, aucun accès au DOM.
 */

const SYMBOLES_DEVISE = {
  EUR: '€',
  USD: '$',
  GBP: '£',
  CHF: 'CHF'
}

/**
 * Transforme une saisie utilisateur en nombre : accepte la virgule décimale,
 * les espaces (y compris insécables) et un symbole de devise collé au montant.
 */
export function normaliserMontant (valeur) {
  if (typeof valeur === 'number') {
    return isFinite(valeur) ? valeur : 0
  }
  if (valeur === null || valeur === undefined) {
    return 0
  }
  const nettoye = String(valeur)
    .replace(/[\s  ]/g, '')
    .replace(/[^\d,.-]/g, '')
    .replace(',', '.')
  const nombre = parseFloat(nettoye)
  return isFinite(nombre) ? nombre : 0
}

function enCentimes (valeur) {
  return Math.round(normaliserMontant(valeur) * 100)
}

/**
 * Le prix unitaire est d'abord converti en centimes, PUIS multiplié par la
 * quantité : multiplier deux flottants avant d'arrondir fait perdre un centime
 * sur des cas aussi banals que 1,5 × 3,33.
 */
function montantLigneCentimes (ligne) {
  if (!ligne) return 0
  return Math.round(enCentimes(ligne.prixUnitaire) * normaliserMontant(ligne.quantite))
}

export function montantLigne (ligne) {
  return montantLigneCentimes(ligne) / 100
}

/**
 * Totaux d'une facture. Les options masquées ne contribuent jamais au total,
 * même si une valeur traîne dans le modèle (bloc refermé puis réouvert).
 */
export function calculerTotaux (facture) {
  const modele = facture || {}
  const options = modele.options || {}
  const lignes = modele.lignes || []

  const centimesLignes = lignes.map(montantLigneCentimes)
  const sousTotal = centimesLignes.reduce(function (somme, centimes) {
    return somme + centimes
  }, 0)

  let remise = 0
  if (options.remise) {
    const parametres = modele.remise || {}
    remise = parametres.type === 'montant'
      ? enCentimes(parametres.valeur)
      : Math.round(sousTotal * normaliserMontant(parametres.valeur) / 100)
    remise = Math.min(Math.max(remise, 0), sousTotal)
  }

  const baseImposable = sousTotal - remise

  let tva = 0
  if (options.tva) {
    const parametres = modele.tva || {}
    if (parametres.mode === 'ligne') {
      tva = centimesLignes.reduce(function (somme, centimes, index) {
        // La remise globale est proratisée sur chaque ligne au prorata de son montant
        const partRemise = sousTotal > 0 ? Math.round(remise * centimes / sousTotal) : 0
        const baseLigne = centimes - partRemise
        const taux = normaliserMontant(lignes[index].tauxTva)
        return somme + Math.round(baseLigne * taux / 100)
      }, 0)
    } else {
      tva = Math.round(baseImposable * normaliserMontant(parametres.taux) / 100)
    }
  }

  const frais = options.frais ? Math.max(enCentimes(modele.frais), 0) : 0
  const total = baseImposable + tva + frais

  return {
    sousTotal: sousTotal / 100,
    montantRemise: remise / 100,
    baseImposable: baseImposable / 100,
    montantTva: tva / 100,
    frais: frais / 100,
    total: total / 100,
    montantsLignes: centimesLignes.map(function (centimes) {
      return centimes / 100
    })
  }
}

/**
 * Formatage belge/français : « 1 234,56 € ».
 * Espace insécable U+00A0 comme séparateur de milliers (et non l'espace fine
 * U+202F, absente de la police Roboto embarquée par pdfmake).
 */
export function formaterMontant (valeur, devise) {
  const nombre = normaliserMontant(valeur)
  const centimes = Math.round(Math.abs(nombre) * 100)
  const entier = String(Math.floor(centimes / 100))
    .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
  const decimales = ('0' + (centimes % 100)).slice(-2)
  const signe = nombre < 0 ? '-' : ''
  const symbole = SYMBOLES_DEVISE[devise] || devise || '€'
  return signe + entier + ',' + decimales + ' ' + symbole
}

export default {
  normaliserMontant,
  montantLigne,
  calculerTotaux,
  formaterMontant
}



// WEBPACK FOOTER //
// ./src/utils/facture/calculs.js